import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { getSuperAdminSchools, SASchoolRow } from '../../services/api';
import SuperAdminLayout from './SuperAdminLayout';

/** Understanding-mix bar — same buckets/colours as the class pages. */
const Ubar: React.FC<{ got: number; almost: number; needs: number; none: number }> = ({ got, almost, needs, none }) => {
  const total = got + almost + needs + none || 1;
  const w = (n: number) => `${(n / total) * 100}%`;
  return (
    <span className="ubar">
      <i className="u-got" style={{ width: w(got) }} />
      <i className="u-part" style={{ width: w(almost) }} />
      <i className="u-not" style={{ width: w(needs) }} />
      <i className="u-exp" style={{ width: w(none) }} />
    </span>
  );
};

/** Task 147 — schools roll-up (Mission Control artifact styling). */
const SuperAdminSchools: React.FC = () => {
  const navigate = useNavigate();
  const [rows, setRows] = useState<SASchoolRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    const token = localStorage.getItem('superAdminToken');
    if (!token) {
      navigate('/super-admin/login', { replace: true });
      return;
    }
    getSuperAdminSchools(token)
      .then(setRows)
      .catch((e) => {
        const msg = e instanceof Error ? e.message : 'Failed to load.';
        setError(msg);
        if (/token|unauth/i.test(msg)) {
          localStorage.removeItem('superAdminToken');
          navigate('/super-admin/login', { replace: true });
        }
      })
      .finally(() => setLoading(false));
  }, [navigate]);

  return (
    <SuperAdminLayout>
      <div className="content">
        <h1 className="pg">Schools</h1>
        <p className="pg-sub">Children and facilitators per school. Click a school to see its classes.</p>

        {loading ? (
          <p className="hint">Loading…</p>
        ) : error ? (
          <p className="hint" style={{ color: 'var(--crit)' }}>{error}</p>
        ) : rows.length === 0 ? (
          <p className="hint">No schools yet.</p>
        ) : (
          <div className="cards">
            {rows.map((s) => (
              <div
                key={s.school}
                className="rowcard"
                onClick={() => navigate(`/super-admin/schools/${encodeURIComponent(s.school)}`)}
              >
                <div className="top"><h4>{s.school}</h4><span className="rowend">›</span></div>
                <div className="split">
                  <span className="kv"><b>{s.students.toLocaleString()}</b>students</span>
                  <span className="kv"><b>{s.active_week}</b>active this wk</span>
                </div>
                <div className="half-lab">Understanding <span className="ai">AI</span></div>
                <Ubar got={s.got} almost={s.almost} needs={s.needs} none={s.no_data} />
                <div className="half-lab">Staff</div>
                <div className="split">
                  <span className="kv"><b>{s.facilitators}</b>facilitators</span>
                </div>
              </div>
            ))}
          </div>
        )}
        {!loading && !error && rows.length > 0 && (
          <div className="ukey">
            <span><i className="u-got" />Got it</span>
            <span><i className="u-part" />Almost</span>
            <span><i className="u-not" />Needs practice</span>
            <span><i className="u-exp" />No data</span>
          </div>
        )}
      </div>
    </SuperAdminLayout>
  );
};

export default SuperAdminSchools;
